import React, { useContext, useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    BackIconWrapper,
    InputWrapper,
    InputContainer,
    StyledTypography,
    StyledInput,
    CHARACTER_LIMIT_CLASS_NAME,
    ErrorMessage,
} from './styles';
import { ReactComponent as BackIcon } from '@/components/Sections/Profile/icons/icon-back.svg';
import { ContentItem, LinkContent } from '@/store/types/yat-page-actions';
import { editItem } from '@/store/actions/yat-page-item-actions';
import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from '@/store/slices/yat-page/yat-page';
import { useAppDispatch } from '@/hooks';

const CHAR_LIMITS = {
    title: TITLE_MAX_LENGTH,
    description: DESCRIPTION_MAX_LENGTH,
};

export type EditingAttribute = 'title' | 'url' | 'description';

interface Props {
    hasBackButton?: boolean;
    editingAttribute: EditingAttribute;
    linkItem: ContentItem<LinkContent>;
    onClose: () => void;
    placeholderText: string;
}

const LinkAttributeInput = ({ hasBackButton = true, editingAttribute, linkItem, onClose, placeholderText }: Props) => {
    const dispatch = useAppDispatch();
    const theme = useContext(ThemeContext);
    const { t } = useTranslation('yat_page');
    const [editingAttributeValue, setEditingAttributeValue] = useState<string>();

    const updateItem = (updatedItem: {
        collapsed?: boolean;
        content?: {
            url?: string;
            description?: string;
            title?: string;
            image?: string;
        };
    }) => {
        if (linkItem && linkItem.content) {
            dispatch(
                editItem({
                    ...linkItem,
                    collapsed: updatedItem.collapsed,
                    content: {
                        ...linkItem.content,
                        ...updatedItem.content,
                    },
                }),
            );
        }
    };

    useEffect(() => {
        if (editingAttribute && linkItem.id) {
            setEditingAttributeValue(linkItem?.content?.[editingAttribute]);
            return;
        }

        setEditingAttributeValue(undefined);
    }, [linkItem.id, editingAttribute, linkItem?.content]);

    const handleChangeValue = (newValue: string) => {
        updateItem({ content: { [editingAttribute]: newValue } });
        setEditingAttributeValue(newValue);
    };

    return (
        <InputWrapper>
            {linkItem.isURLInvalid && editingAttribute === 'url' && (
                <StyledTypography type="label" weight="400" size={12}>
                    <ErrorMessage>{t('invalid_url_address')}</ErrorMessage>
                </StyledTypography>
            )}
            <InputContainer>
                <AnimatePresence initial={false}>
                    {hasBackButton && (
                        <BackIconWrapper
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: theme.transitions.speeds.normal,
                                ease: theme.transitions.eases.cubic,
                            }}
                            onClick={onClose}
                        >
                            <BackIcon />
                        </BackIconWrapper>
                    )}
                </AnimatePresence>
                <StyledTypography type="paragraph" weight="400" size={16} color="white">
                    <StyledInput
                        autoFocus
                        value={editingAttributeValue || ''}
                        multipleLines={editingAttribute !== 'url'}
                        $multiline={editingAttribute !== 'url'}
                        $isURLInvalid={editingAttribute === 'url' && linkItem.isURLInvalid}
                        characterLimit={CHAR_LIMITS[editingAttribute]}
                        characterLimitClassName={CHARACTER_LIMIT_CLASS_NAME}
                        onChange={handleChangeValue}
                        onBlur={onClose}
                        onEnterKeyDown={onClose}
                        placeholderText={placeholderText}
                    />
                </StyledTypography>
            </InputContainer>
        </InputWrapper>
    );
};

export default LinkAttributeInput;
